import React, { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar } from 'components/UI';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import Images from 'assets/images/imageData';
import localization from 'assets/localization/localization';
import { toast } from 'react-toastify';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import {
  getDarwinRecordData,
  getMonthsFilter,
  parseMonthFilterId,
} from 'services/darwinRecord/darwinRecordApi';
import DarwinRecordCard from './components/DarwinRecordCard';
import {
  buildQueryString,
  parseQueryParams,
  readStoredMonth,
  writeStoredMonth,
} from './darwinRecordUtils';
import './darwinRecord.css';

const pickMonth = (options, preferred) => {
  if (preferred && options.some((option) => option.id === preferred)) {
    return preferred;
  }
  return options[options.length - 1]?.id ?? '';
};

const DarwinRecordList = ({ history, location }) => {
  const { baseUrl } = useContext(BaseUrl);
  const [monthOptions, setMonthOptions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [monthsLoading, setMonthsLoading] = useState(true);
  const [showFilter, setShowFilter] = useState(false);

  const defaultMonth = useMemo(
    () => monthOptions[monthOptions.length - 1]?.id ?? '',
    [monthOptions]
  );

  const persistMonth = (monthId) => {
    writeStoredMonth(monthId);
    const nextPath = `${baseUrl}/darwin-record${buildQueryString({
      month: monthId,
    })}`;
    const currentPath = `${location?.pathname || ''}${location?.search || ''}`;
    if (monthId && currentPath !== nextPath) {
      history.replace(nextPath);
    }
  };

  const selectedMonthLabel =
    monthOptions.find((month) => month.id === selectedMonth)?.name ?? selectedMonth;

  const appliedFilterLabels = selectedMonth
    ? [`Month: ${selectedMonthLabel}`]
    : [];

  const fetchMonths = async () => {
    setMonthsLoading(true);
    const { data, error } = await getMonthsFilter();
    if (data && data.length > 0) {
      setMonthOptions(data);
      const preferred =
        parseQueryParams(location?.search).month || readStoredMonth();
      const nextMonth = pickMonth(data, preferred);
      setSelectedMonth(nextMonth);
      persistMonth(nextMonth);
    } else {
      setMonthOptions([]);
      setIsLoading(false);
      toast.error(error || 'Unable to load months');
    }
    setMonthsLoading(false);
  };

  const fetchRecords = async (monthId) => {
    const parsed = parseMonthFilterId(monthId);
    if (!parsed) {
      setIsLoading(false);
      toast.error('Invalid month selected');
      return;
    }

    setIsLoading(true);
    const { data, error } = await getDarwinRecordData(parsed);
    if (error) {
      setRecords([]);
      toast.error(error);
    } else {
      setRecords(data || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchMonths();
  }, []);

  useEffect(() => {
    if (selectedMonth) {
      fetchRecords(selectedMonth);
    }
  }, [selectedMonth]);

  const filterToggle = () => setShowFilter(!showFilter);

  const applyFilter = (monthId) => {
    const nextMonth = monthId || defaultMonth;
    setSelectedMonth(nextMonth);
    persistMonth(nextMonth);
    filterToggle();
  };

  const resetFilters = () => {
    setSelectedMonth(defaultMonth);
    persistMonth(defaultMonth);
  };

  const headerBtns = () => (
    <Fragment>
      <div className="darwin-filter-btn-wrap">
        <Button
          onClick={filterToggle}
          className="filter-hamburger btn btn-transparent"
          disabled={monthsLoading || monthOptions.length === 0}
        >
          <img src={Images?.Filter} alt="" />
          {localization.showFilters}
        </Button>
        {appliedFilterLabels.length > 0 && <span className="darwin-filter-dot" />}
      </div>
    </Fragment>
  );

  return (
    <Layout
      title="Attendance Record"
      isLoading={isLoading || monthsLoading}
      Buttons={headerBtns}
    >
      <div className="darwin-record-page">
        <AppliedFiltersBar
          items={appliedFilterLabels}
          onClear={resetFilters}
          clearButtonLabel="Reset month"
        />

        {records.length === 0 && !isLoading && !monthsLoading ? (
          <div className="darwin-empty-state">
            <p className="darwin-empty-state__title">No attendance records found</p>
            <p className="darwin-empty-state__subtitle">
              Try selecting a different month.
            </p>
          </div>
        ) : (
          <div className="darwin-record-grid">
            {records.map((item) => (
              <DarwinRecordCard
                key={item.dateStr}
                item={item}
                baseUrl={baseUrl}
                history={history}
              />
            ))}
          </div>
        )}
      </div>

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={applyFilter}
        darwinRecordFilter
        darwinRecordFilterProps={{
          monthOptions,
          selectedMonth,
          defaultMonth,
        }}
      />
    </Layout>
  );
};

export default DarwinRecordList;
